import React, { useState, useEffect } from "react";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import { useDispatch, useSelector } from "react-redux";
import { getAllClients } from "../../service/client/ClientServices";
import { ClientDialog } from "./ClientDialog";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { CreateInvoiceDto } from "@/types/invoice.types";
import { invoiceData } from "@/data/invoiceData";

interface ClientSelectorProps {
  value: any;
  onChange: (e: any) => void;
  preselectedClient?: any;
  className?: string;
  invoiceData: CreateInvoiceDto;
  setInvoiceData: (invoice: CreateInvoiceDto) => void;
}

interface Client {
  id: number;
  name: string;
  email?: string;
  contactPerson?: string;
  phone?: string;
  address?: {
    line1: string;
    line2?: string;
    city: string;
    stateProvince?: string;
    postalCode?: string;
    country: string;
  };
  taxNumber?: string;
  taxExempt: boolean;
}

export const ClientSelector: React.FC<ClientSelectorProps> = ({
  value,
  onChange,
  preselectedClient,
  className = "",
  invoiceData,
  setInvoiceData
}) => {
  const dispatch = useDispatch();
  const localization = useSelector((state: any) => state.localization);
  const [clients, setClients] = useState<Client[]>([]);
  const [dialogVisible, setDialogVisible] = useState<boolean>(false);
  const [countries, setCountries] = useState
    <{ label: string; value: number }[]
  >([
    { label: "United States", value: 1 },
    { label: "Canada", value: 2 },
    { label: "United Kingdom", value: 3 },
    { label: "Australia", value: 4 },
    { label: "Germany", value: 5 },
  ]);

  useEffect(() => {
    fetchClients();
  }, []);

  useEffect(() => {
    if (preselectedClient) {
      const clientExists = clients.some(
        (c: Client) => c.id === preselectedClient.id
      );

      if (!clientExists && clients.length > 0) {
        setClients([...clients, preselectedClient]);
      }
    }
  }, [preselectedClient, clients]);

  const fetchClients = async () => {
    try {
      const response = await getAllClients(dispatch, {
        Page: 1,
        Limit: 100,
        SortBy: "ASC",
        SortProperty: "client_name",
      });

      if (response) {
        const formattedClients = response?.List?.map((client: any) => {
          const paymentTerm = client.payment_term_id;

          return {
            id: client.client_id,
            name: client.client_name,
            email: client.email,
            contactPerson: client.contact_person,
            phone: client.phone,
            address: {
              line1: client.address_line1,
              line2: client.address_line2,
              city: client.city,
              stateProvince: client.state_province,
              postalCode: client.postal_code,
              country: client.country_name,
            },
            taxNumber: client.tax_number,
            taxExempt: client.tax_exempt,
            currency_code: client.currency_code || "USD",
            payment_terms: paymentTerm,
            business_type: client.business_type_id,
            industry_id: client.industry_id,
          }
        });

        setClients(formattedClients);

        if (preselectedClient) {
          const clientExists = formattedClients.some(
            (c: Client) => c.id === preselectedClient.id
          );

          if (!clientExists) {
            setClients([...formattedClients, preselectedClient]);
          }
        }
      }
    } catch (error) {
      console.error("Error fetching clients:", error);
    } 
  };

  const handleClientCreated = (newClient: any) => {
    setClients([...clients, newClient]);
    onChange({
      value: newClient,
      originalEvent: { target: { name: "client" } },
    });
  };

  const clientTemplate = (option: any) => {
    return (
      <div className="flex flex-col">
        <span className="font-medium">{option.name}</span>
        {option.email && (
          <span className="text-xs text-gray-500">{option.email}</span>
        )}
      </div>
    );
  };

  const selectedClientTemplate = (option: any, props: any) => {
    if (option) {
      return (
        <div className="flex flex-col">
          <span className="font-medium">{option.name}</span>
          {option.email && (
            <span className="text-xs text-gray-500">{option.email}</span>
          )}
        </div>
      );
    }

    return <span>{props.placeholder}</span>;
  };

  return (
    <div className="flex items-center">
      <Dropdown
        value={value}
        options={clients}
        onChange={onChange}
        optionLabel="name"
        filter
        dataKey="id"
        filterBy="name,email"
        placeholder={localization?.SelectAClient || "Select a Client"}
        className={`w-full ${className}`}
        itemTemplate={clientTemplate}
        valueTemplate={selectedClientTemplate}
      />
      <Button
        className="rounded-sm border-2 border-gold bg-gold text-white px-4 py-2 hover:bg-white hover:text-gold transition-colors duration-300 ml-2"
        onClick={() => setDialogVisible(true)}
        tooltip={localization?.CreateNewClient || "Create New Client"}
        tooltipOptions={{ position: "top" }}
        type="button"
      >
        <FontAwesomeIcon icon={faPlus} />
      </Button>

      <ClientDialog
        visible={dialogVisible}
        onHide={() => setDialogVisible(false)}
        onClientCreated={handleClientCreated}
        countries={countries}
      />
    </div>
  );
};